/* =========================================================
   Flexi › Vernerunder (safety rounds) — TS models.
   Shapes mirror the backend DTOs under
   Backend/Kuba2.Client.Entities/Flexi/SafetyRounds/. JSON is PascalCase and
   enums travel as numbers. Calendar dates (RoundDate, Deadline) are
   "yyyy-MM-dd"; instants (CompletedOn, ModifiedOn, ...) are UTC ISO strings.
   ========================================================= */

/** Grade of one checkpoint. `null` on the wire means not graded yet. */
export enum SafetyRoundGrade {
  NotApplicable = 0,
  Satisfactory = 1,
  ShouldImprove = 2,
  LegalBreach = 3,
  DangerToLifeOrHealth = 4,
}

export enum SafetyRoundStatus {
  InProgress = 0,
  Completed = 1,
}

export enum SafetyRoundTemplateStatus {
  Draft = 0,
  Published = 1,
  Archived = 2,
}

/** Deviation importance as stored by the deviation module. */
export enum DeviationImportance {
  Low = 1,
  Medium = 2,
  High = 3,
}

/** Suggested deadline, in days from the round, for each finding grade. */
export interface SafetyRoundDueDays {
  ShouldImprove: number;
  LegalBreach: number;
  Danger: number;
}

export interface RegulatoryReference {
  Title: string;
  Url: string | null;
}

// ---------------------------------------------------------------------------
// Business routes — api/kuba/v1/flexi/safety-rounds
// ---------------------------------------------------------------------------

export interface SafetyRoundSummary {
  Id: number;
  Title: string;
  RoundDate: string;
  Location: string | null;
  TemplateName: string | null;
  ResponsibleUserName: string | null;
  Status: SafetyRoundStatus;
  GradedCount: number;
  ItemCount: number;
  ShouldImproveCount: number;
  LegalBreachCount: number;
  DangerCount: number;
  /** Linked deviations not yet Done, Closed or Rejected. */
  OpenDeviationCount: number;
  CompletedOn: string | null;
}

/** A template the business may start a round from. */
export interface AssignedSafetyRoundTemplate {
  Id: number;
  Name: string;
  Version: number;
  ItemCount: number;
  SectionCount: number;
  IsDefault: boolean;
  /** RoundDate of the business's latest round started from this template. */
  LastUsedOn: string | null;
}

export interface StartSafetyRoundRequest {
  /** Null starts a round without template, and so without checkpoints. */
  TemplateId: number | null;
  /** Null lets the backend name it "Vernerunde dd.MM.yyyy". */
  Title: string | null;
}

/** The deviation a finding was followed up with. */
export interface SafetyRoundItemDeviation {
  Id: number;
  /** Display number, e.g. "GN-1043". */
  Number: string;
  Status: number | null;
  Importance: DeviationImportance | null;
  Deadline: string | null;
  ExecutionUserName: string | null;
}

/** What the caller may do in one business's safety rounds. */
export interface SafetyRoundPermissions {
  /** Admin or a listed HSE (BHT) advisor of the business; never the business itself. */
  CanAuthor: boolean;
}

/** A photo on a checkpoint. */
export interface SafetyRoundPhoto {
  Id: number;
  FileName: string;
  /** Short-lived read link (about 30 minutes); re-read the round to refresh it. */
  Url: string;
}

export interface SafetyRoundItem {
  Id: number;
  SectionName: string;
  SectionPosition: number;
  Position: number;
  Title: string;
  Guidance: string | null;
  RegulatoryReferences: RegulatoryReference[];
  StandardTexts: string[];
  RequirePhotoAtGrade3: boolean;
  RequirePhotoAtGrade4: boolean;
  /** Added during the round rather than copied from the template. */
  IsAdHoc: boolean;
  Grade: SafetyRoundGrade | null;
  Comment: string | null;
  Deviation: SafetyRoundItemDeviation | null;
  /** Attached to the deviation automatically when it is created. */
  Photos: SafetyRoundPhoto[];
}

export interface SafetyRoundDetail {
  Id: number;
  Title: string;
  RoundDate: string;
  Location: string | null;
  BusinessDepartmentId: number | null;
  ResponsibleUserId: number | null;
  ResponsibleUserName: string | null;
  Participants: string | null;
  Summary: string | null;
  TemplateName: string | null;
  Status: SafetyRoundStatus;
  CompletedOn: string | null;
  DueDays: SafetyRoundDueDays;
  /** Ordered by SectionPosition, then Position. */
  Items: SafetyRoundItem[];
}

export interface UpdateSafetyRoundDetailsRequest {
  Title: string;
  Location: string | null;
  BusinessDepartmentId: number | null;
  ResponsibleUserId: number | null;
  Participants: string | null;
  Summary: string | null;
}

export interface GradeSafetyRoundItemRequest {
  Grade: SafetyRoundGrade | null;
  Comment: string | null;
}

export interface AddSafetyRoundItemRequest {
  SectionName: string;
  Title: string;
}

export interface MarkNotApplicableRequest {
  /** Null marks every ungraded item of the round. */
  SectionName: string | null;
}

/** Same attachment shape as the deviation module's create request. */
export interface SafetyRoundDeviationAttachment {
  Id?: number;
  Filename?: string;
  OriginalFileName?: string;
  MimeType?: string;
  Size?: number;
  Path?: string;
  FileType?: number;
  Status?: number;
}

export interface CreateSafetyRoundFindingDeviationRequest {
  ExecutionUserId: number;
  /** "yyyy-MM-dd". */
  Deadline: string;
  TopicId: number;
  CaseId: number;
  /** Required for grade 4. */
  ImmediateAction: string | null;
  /** Empty means the round's department. */
  BusinessDepartmentIds: number[];
  FollowupUserId: number | null;
  /** Kept empty: the backend attaches the checkpoint's photos itself. */
  DeviationAttachments: SafetyRoundDeviationAttachment[];
}

export interface CompleteSafetyRoundRequest {
  Summary: string | null;
  /** Must be true. */
  Confirmed: boolean;
}

// ---------------------------------------------------------------------------
// Admin routes — api/kuba/v1/flexi/safety-round-templates
// ---------------------------------------------------------------------------

export interface SafetyRoundTemplateSummary {
  Id: number;
  Name: string;
  Industry: string | null;
  Status: SafetyRoundTemplateStatus;
  Version: number;
  ItemCount: number;
  SectionCount: number;
  AssignedCount: number;
  IsDefaultForNewBusinesses: boolean;
  ModifiedOn: string | null;
  CreatedOn: string;
}

export interface SafetyRoundTemplateItem {
  SectionName: string;
  SectionPosition: number;
  Position: number;
  Title: string;
  Guidance: string | null;
  StandardTexts: string[];
  RegulatoryReferences: RegulatoryReference[];
  RequirePhotoAtGrade3: boolean;
  RequirePhotoAtGrade4: boolean;
}

export interface SafetyRoundTemplateDetail {
  Id: number;
  Name: string;
  Industry: string | null;
  Description: string | null;
  Status: SafetyRoundTemplateStatus;
  Version: number;
  IsDefaultForNewBusinesses: boolean;
  DueDays: SafetyRoundDueDays;
  Items: SafetyRoundTemplateItem[];
}

export interface CreateSafetyRoundTemplateRequest {
  Name: string;
  Industry: string | null;
  Description: string | null;
}

export interface SaveSafetyRoundTemplateRequest {
  Name: string;
  Industry: string | null;
  Description: string | null;
  DueDays: SafetyRoundDueDays;
  Items: SafetyRoundTemplateItem[];
}

export interface SafetyRoundTemplateAssignment {
  BusinessId: number;
  BusinessName: string;
  IsDefault: boolean;
  AssignedOn: string;
}

export interface SafetyRoundBusinessCandidate {
  BusinessId: number;
  BusinessName: string;
  HasTemplate: boolean;
}

export interface AssignSafetyRoundTemplateRequest {
  BusinessIds: number[];
  MakeDefault: boolean;
}

// ---------------------------------------------------------------------------
// Lookups reused from the deviation module (legacy endpoints, PascalCase).
// ---------------------------------------------------------------------------

/** One option of a lookup list (users, topics, case types, departments). */
export interface SafetyRoundLookupOption {
  label: string;
  value: number;
}
